export const CATEGORY_PRODUCT_COUNTS_SCHEMA_VERSION = 1;

export type CategoryProductCountNode = {
    categoryId: string;
    name: string;
    productCount: number;
    url?: string | null;
    children: CategoryProductCountNode[];
};

export type CategoryProductCounts = {
    schemaVersion: number;
    generatedAt: string;
    siteId: string;
    trees: CategoryProductCountNode[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalStringOrNull(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'string';
}

function isCategoryProductCountNode(value: unknown): value is CategoryProductCountNode {
    if (!isRecord(value) || !Array.isArray(value.children)) {
        return false;
    }
    return (
        typeof value.categoryId === 'string' &&
        typeof value.name === 'string' &&
        typeof value.productCount === 'number' &&
        isOptionalStringOrNull(value.url) &&
        value.children.every(isCategoryProductCountNode)
    );
}

/** Validates an already-parsed category product counts payload; `undefined` when unsupported or malformed. */
export function toCategoryProductCounts(parsed: unknown): CategoryProductCounts | undefined {
    if (!isRecord(parsed) || parsed.schemaVersion !== CATEGORY_PRODUCT_COUNTS_SCHEMA_VERSION) {
        return undefined;
    }
    if (typeof parsed.generatedAt !== 'string' || typeof parsed.siteId !== 'string' || !Array.isArray(parsed.trees)) {
        return undefined;
    }
    if (!parsed.trees.every(isCategoryProductCountNode)) {
        return undefined;
    }

    return {
        schemaVersion: parsed.schemaVersion,
        generatedAt: parsed.generatedAt,
        siteId: parsed.siteId,
        trees: parsed.trees,
    };
}

function collectProductCounts(nodes: CategoryProductCountNode[], byId: Map<string, number>): void {
    for (const node of nodes) {
        byId.set(node.categoryId, node.productCount);
        if (node.children.length) {
            collectProductCounts(node.children, byId);
        }
    }
}

/** Flatten catalog trees into categoryId → productCount. */
export function getCategoryProductCountById(
    counts: CategoryProductCounts | undefined
): Map<string, number> | undefined {
    if (!counts) {
        return undefined;
    }

    const byId = new Map<string, number>();
    collectProductCounts(counts.trees, byId);
    return byId;
}

/**
 * True when the counts map records this category with zero products.
 * A missing map or an unknown id stays visible (same fail-open as the navigation menu).
 */
export function isCategoryWithoutProducts(
    productCountById: Map<string, number> | undefined,
    categoryId: string
): boolean {
    return productCountById?.get(categoryId) === 0;
}

/** Stable resolved promise used when the layout loader did not provide counts. */
export const emptyCategoryProductCounts: Promise<CategoryProductCounts | undefined> = Promise.resolve(undefined);
